/**
 * Collaboration Definition Types
 *
 * These types mirror the CollaborationDefinition types from innovate-service
 * for use in the definition builder UI.
 */

// =============================================================================
// Core Definition Structure
// =============================================================================

/**
 * MIRROR of the service's `CollaborationDefinition`
 * (`innovate-service/src/types/domain/collaborationDefinition.ts`), which is the
 * source of truth. Keep the two in step: when the mirror drifts, the canvas
 * round-trip has no type-level reason to keep a field it never heard of.
 *
 * That drift is exactly the task-877 defect — the service declared
 * `credentialPassthrough` and this mirror did not, so nothing flagged the canvas
 * save that deleted it. `reactFlowToCollaboration` now preserves unknown keys
 * regardless, but the mirror is the half that makes the field editable and
 * visible rather than merely surviving.
 */
export interface CollaborationDefinition {
  version: '1.0';
  nodes: CollaborationNode[];
  edges: CollaborationEdge[];
  settings?: CollaborationSettings;
  context?: Record<string, unknown>;
  errorHandling?: ErrorHandlingConfig;
  /**
   * Which nodes run as the CALLER rather than as the service principal
   * (task 135/755). Security-relevant: a node listed here reaches self-MCP with
   * the User's forwarded credential, and a node that is not listed gets none.
   * Default-deny — an absent declaration forwards nothing to anyone.
   */
  credentialPassthrough?: { enabled?: boolean; nodeIds?: string[] };
}

// =============================================================================
// Edge Definition
// =============================================================================

export interface CollaborationEdge {
  id?: string;
  from: string;
  to: string;
  condition?: string;
  label?: string;
}

// =============================================================================
// Node Types
// =============================================================================

export type CollaborationNode = AgentNode | FunctionNode | BranchNode | EntryNode;

export type NodeType = 'agent' | 'function' | 'branch' | 'entry';

interface NodeBase {
  id: string;
  name?: string;
  type: NodeType;
  entry?: boolean;
  input?: Record<string, unknown>;
  output?: Record<string, unknown>;
  inputMapping?: Record<string, string>;
  // UI-specific: position for ReactFlow
  position?: { x: number; y: number };
}

export interface AgentNode extends NodeBase {
  type: 'agent';
  agentId: string;
  alias?: string;
  llmConfigId?: string;
  autonomy?: AutonomySettings;
}

export interface FunctionNode extends NodeBase {
  type: 'function';
  function: string;
  config?: Record<string, unknown>;
}

export type BranchType = 'conditional' | 'parallel' | 'loop';

export interface BranchNode extends NodeBase {
  type: 'branch';
  branchType: BranchType;
  condition?: string;
  until?: string;
  branches?: BranchDefinition[];
  /**
   * Conditional routing decisions — the form seeded branches actually use
   * (task 774). The engine calls this `decisions` and types each entry as
   * `DecisionDefinition` (`innovate-service/src/types/domain/collaborationDefinition.ts`),
   * whose field set is identical to `BranchDefinition` below, so the shape is
   * reused rather than duplicated.
   *
   * A branch routes EITHER by a top-level `condition` or by these; an entry
   * with no `condition` is the engine's explicit default arm, not an omission.
   */
  decisions?: BranchDefinition[];
  nodes?: CollaborationNode[];
  edges?: CollaborationEdge[];
}

/**
 * One routing arm of a branch. Mirrors the engine's `DecisionDefinition`
 * field-for-field — `condition` is optional there because a decision without
 * one is the default arm.
 */
export interface BranchDefinition {
  id: string;
  name?: string;
  condition?: string;
  targetNodeId: string;
}

export interface EntryNode extends NodeBase {
  type: 'entry';
  prompt?: string;
}

// =============================================================================
// Settings
// =============================================================================

export interface AutonomySettings {
  maxIterations?: number;
  confidenceThreshold?: number;
  qualityThreshold?: number;
  satisfactionCheck?: boolean;
  allowDataRequests?: boolean;
}

export interface CollaborationSettings {
  timeout?: number;
  logging?: {
    level?: 'debug' | 'info' | 'warn' | 'error';
    includeInput?: boolean;
    includeOutput?: boolean;
  };
  retry?: {
    maxRetries?: number;
    backoffMs?: number;
    retryableErrors?: string[];
  };
}

export interface ErrorHandlingConfig {
  strategy: 'fail' | 'skip' | 'fallback' | 'retry';
  fallbackNodeId?: string;
  continueOnError?: boolean;
}

// =============================================================================
// Type Guards
// =============================================================================

export const isAgentNode = (node: CollaborationNode): node is AgentNode => {
  return node.type === 'agent';
};

export const isFunctionNode = (node: CollaborationNode): node is FunctionNode => {
  return node.type === 'function';
};

export const isBranchNode = (node: CollaborationNode): node is BranchNode => {
  return node.type === 'branch';
};

export const isEntryNode = (node: CollaborationNode): node is EntryNode => {
  return node.type === 'entry';
};
